/**
 * Privacy — read-only POPIA consent notice (web port).
 *
 * Surfaces the active privacy/consent text the user agreed to (or the latest
 * effective version) by fetching GET /consent/current. Web counterpart of the
 * consent step rendered in apps/mobile/app/signup.tsx — but read-only: no
 * scroll-to-accept gate and no acceptance POST. Acceptance is recorded
 * implicitly at signup; this screen is just a reference users can revisit.
 *
 * The body is markdown (`body_md`), rendered via `react-markdown` (base, no
 * plugins) inside a theme-styled wrapper so headings/paragraphs/lists/links
 * format correctly. The render is integrity-preserving — the exact `body_md`
 * is passed through unaltered (TD-080).
 *
 * Contract verified against services/api/src/consent/dto.ts +
 * consent.controller.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';

import { consentControllerGetCurrent } from '../api/generated/consent/consent';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

/** Format an ISO date (YYYY-MM-DD) for display; fall back to the raw string. */
function formatEffectiveFrom(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : dateFormatter.format(parsed);
}

export function PrivacyScreen() {
  const consentQuery = useQuery({
    queryKey: ['consent', 'current'],
    queryFn: ({ signal }) => consentControllerGetCurrent(signal),
  });

  const consent = consentQuery.data;

  return (
    <Stack spacing={3} sx={{ maxWidth: 760 }}>
      <Box>
        <Text variant="headline-medium" as="h1">
          Privacy &amp; consent
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            The POPIA privacy notice currently in effect. This is the text you agreed to when you
            signed up.
          </Text>
        </Box>
      </Box>

      {consentQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : consentQuery.isError || !consent ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => consentQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Could not load the privacy notice. {String(consentQuery.error)}
        </Alert>
      ) : (
        <Card>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Text variant="title-medium" as="h2">
                POPIA privacy notice
              </Text>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', mt: 1, flexWrap: 'wrap' }}
              >
                <Chip kind="assist" color="success" label={`Version ${consent.version}`} />
                <Text variant="label-medium" color="text.secondary">
                  Effective from {formatEffectiveFrom(consent.effective_from)}
                </Text>
              </Stack>
            </Box>
            {/* Markdown body (`body_md`) rendered via react-markdown. The sx
                below styles the rendered elements to the MUI theme; the exact
                body_md is passed through unaltered (integrity-preserving). */}
            <Box
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: 'text.primary',
                '& > :first-of-type': { mt: 0 },
                '& > :last-child': { mb: 0 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontWeight: 600,
                  lineHeight: 1.3,
                  mt: 2.5,
                  mb: 1,
                },
                '& h1': { fontSize: '1.5rem' },
                '& h2': { fontSize: '1.25rem' },
                '& h3': { fontSize: '1.1rem' },
                '& h4, & h5, & h6': { fontSize: '1rem' },
                '& p': { my: 1 },
                '& ul, & ol': { my: 1, pl: 3 },
                '& li': { mb: 0.5 },
                '& a': { color: 'primary.main' },
                '& strong': { fontWeight: 600 },
                '& blockquote': {
                  my: 1,
                  pl: 2,
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                  color: 'text.secondary',
                },
                '& code': {
                  fontFamily: 'monospace',
                  fontSize: '0.85em',
                  bgcolor: 'action.hover',
                  px: 0.5,
                  borderRadius: 0.5,
                },
                '& hr': {
                  my: 2,
                  border: 0,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <ReactMarkdown>{consent.body_md}</ReactMarkdown>
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
